import React, { useEffect, useMemo, useState } from "react";
import Datatable from "../../../core/common/dataTable";
import { ColumnsType } from "antd/es/table";
import { getSearchableHeader } from "../../../core/utils/getSearchableHeader";
import Footer from "../../../core/common/Footer";
import useApi from "../../../core/customhook/useApi";
import { displayValue } from "../../../core/common/TabelValue";
import { PageBreadcrumb } from "../../../core/common/BreadCrumbForm";
import { safeLocaleCompare } from "../../../core/methods/sorterMethods";
import webservice_url from "../../../core/webservice/Webservice_url";
import { PaginationType } from "../../../types/common-type";
import { PageLoader } from "../../../core/common/loader/PageLoader";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import { useLocation } from "react-router";
import UserDetailsSection from "./UserDetailsSection";
import Swal from "sweetalert2";

const breadcrumbItem = [{ label: "Assign LOB" }];

interface SelectedLOB {
  org_id: string | number;
  project_id: string | number;
  program_id: string | number;
  department_id: string | number;
}

const AddMoreLOB = () => {
  const [allItemList, setAllItemList] = useState<any[]>([]);
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});

  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 0,
  });

  const [selectedLobs, setSelectedLobs] = useState<SelectedLOB[]>([]);

  const location = useLocation();

  const currentManagerData = location.state;

  // ================= API =================

  const { callApi, loading } = useApi<any>(webservice_url.get_lob_list, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const assignlobAPI = useApi<any>(webservice_url.assign_program_to_manager, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  // ================= FILTERED DATA =================

  const filteredData = useMemo(() => {
    return allItemList?.filter((row) =>
      Object.entries(columnSearch).every(([key, value]) =>
        value
          ? row[key as keyof typeof row]
              ?.toString()
              ?.toLowerCase()
              ?.includes(value?.toLowerCase())
          : true,
      ),
    );
  }, [allItemList, columnSearch]);

  // ================= SELECTED IDS =================

  const getLobSelection = (record: any): SelectedLOB => ({
    org_id: record.org_id,
    project_id: record.project_id,
    program_id: record.program_id,
    department_id: record.department_id,
  });

  // Check whether current row is selected
  const isLOBSelected = (record: any) => {
    return selectedLobs.some(
      (item) =>
        String(item.org_id) === String(record.org_id) &&
        String(item.project_id) === String(record.project_id) &&
        String(item.program_id) === String(record.program_id) &&
        String(item.department_id) === String(record.department_id),
    );
  };

  const handleSelectLOB = (record: any, checked: boolean) => {
    const lobData = getLobSelection(record);
    setSelectedLobs((prev) => {
      if (checked) {
        const alreadySelected = prev.some(
          (item) =>
            String(item.org_id) === String(lobData.org_id) &&
            String(item.project_id) === String(lobData.project_id) &&
            String(item.program_id) === String(lobData.program_id) &&
            String(item.department_id) === String(lobData.department_id),
        );

        if (alreadySelected) {
          return prev;
        }

        return [...prev, lobData];
      }

      return prev.filter(
        (item) =>
          !(
            String(item.org_id) === String(lobData.org_id) &&
            String(item.project_id) === String(lobData.project_id) &&
            String(item.program_id) === String(lobData.program_id) &&
            String(item.department_id) === String(lobData.department_id)
          ),
      );
    });
  };

  // ================= SELECT ALL =================

  const isAllSelected =
    filteredData.length > 0 &&
    filteredData.every((record: any) => isLOBSelected(record));

  const handleSelectAllLOB = (checked: boolean) => {
    if (checked) {
      const currentPageLobs = filteredData.map(getLobSelection);

      setSelectedLobs((prev) => {
        const newItems = currentPageLobs.filter(
          (newItem) =>
            !prev.some(
              (item) =>
                String(item.org_id) === String(newItem.org_id) &&
                String(item.project_id) === String(newItem.project_id) &&
                String(item.program_id) === String(newItem.program_id) &&
                String(item.department_id) === String(newItem.department_id),
            ),
        );

        return [...prev, ...newItems];
      });
    } else {
      setSelectedLobs((prev) =>
        prev.filter(
          (item) =>
            !filteredData.some(
              (record: any) =>
                String(item.org_id) === String(record.org_id) &&
                String(item.project_id) === String(record.project_id) &&
                String(item.program_id) === String(record.program_id) &&
                String(item.department_id) === String(record.department_id),
            ),
        ),
      );
    }
  };

  // ================= ASSIGN LOB =================

  // const handleAssignLOB = async () => {
  //   try {
  //     if (!currentManagerData?.id) {
  //       console.error("Manager ID is missing");
  //       return;
  //     }

  //     if (selectedLobs.length === 0) {
  //       console.error("Please select at least one LOB");
  //       return;
  //     }

  //     const payload = {
  //       manager_id: currentManagerData.id,
  //       agency_user_profile_id: 2,
  //       lob: selectedLobs,
  //     };

  //     const res = await assignlobAPI.callApi(payload);

  //     if (res?.success) {
  //       // Optional: clear selected rows after successful assignment
  //       setSelectedLobs([]);
  //     }
  //   } catch (error) {
  //     console.error("Assign LOB Error:", error);
  //   }
  // };

  const handleAssignLOB = async () => {
    try {
      if (!currentManagerData?.id) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Manager ID is missing.",
        });
        return;
      }

      if (selectedLobs.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No LOB Selected",
          text: "Please select at least one LOB.",
        });
        return;
      }

      const payload = {
        manager_id: currentManagerData.id,
        agency_user_profile_id: 3,
        lob: selectedLobs,
      };

      const res = await assignlobAPI.callApi(payload);

      if (res?.success) {
        await Swal.fire({
          icon: "success",
          title: "LOB Assigned Successfully",
          text:
            res?.message || "Selected LOBs have been assigned successfully.",
          confirmButtonText: "OK",
        });

        setSelectedLobs([]);
        fetchData();
      } else {
        Swal.fire({
          icon: "error",
          title: "Assignment Failed",
          text: res?.message || "Unable to assign the selected LOBs.",
        });
      }
    } catch (error: any) {
      console.error("Assign LOB Error:", error);

      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to assign LOBs. Please try again.",
      });
    }
  };

  // ================= COLUMNS =================

  const columns: ColumnsType<any> = [
    {
      title: "S.No",
      dataIndex: "s_no",
      align: "center",
      render: (_text: string, _record: any, index: number) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
      sorter: false,
    },

    {
      title: getSearchableHeader("Organization", "name", setColumnSearch),
      dataIndex: "name",
      render: displayValue,
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "name"),
    },

    {
      title: getSearchableHeader("Account", "project_name", setColumnSearch),
      dataIndex: "project_name",
      render: displayValue,
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "project_name"),
    },

    {
      title: getSearchableHeader(
        "Program Name",
        "program_name",
        setColumnSearch,
      ),
      dataIndex: "program_name",
      render: displayValue,
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "program_name"),
    },

    {
      title: getSearchableHeader(
        "Department Name",
        "department_name",
        setColumnSearch,
      ),
      dataIndex: "department_name",
      render: displayValue,
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "department_name"),
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      onCell: () => cellWidth(140),

      render: (text: boolean) => {
        return (
          <span
            className="badge rounded-pill d-inline-flex align-items-center gap-1 px-3 py-2 fw-medium"
            style={{
              background: text ? "#E6F7ED" : "#FDECEB",
              color: text ? "#0F9D58" : "#D93025",
            }}
          >
            <span
              className="rounded-circle"
              style={{
                width: 6,
                height: 6,
                background: text ? "#0F9D58" : "#D93025",
              }}
            />

            {text ? "Active" : "Inactive"}
          </span>
        );
      },

      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "status"),
    },

    // ================= SELECT =================

    {
      title: (
        <div className="d-flex align-items-center justify-content-center gap-2">
          <input
            type="checkbox"
            className="form-check-input"
            checked={isAllSelected}
            onChange={(e) => handleSelectAllLOB(e.target.checked)}
          />

          <span>Select</span>
        </div>
      ),

      key: "select",
      dataIndex: "select",
      align: "center",
      width: 100,

      render: (_: any, record: any) => (
        <input
          type="checkbox"
          className="form-check-input"
          checked={isLOBSelected(record)}
          onChange={(e) => handleSelectLOB(record, e.target.checked)}
        />
      ),
    },
  ];
 
  const fetchData = async () => {
    try {
      const res = await callApi({
        page: pagination?.current ?? 1,
        limit: pagination?.pageSize ?? 10,
      });

      if (res?.success) {
        const data = res?.data?.rows || [];

        setAllItemList(data);

        setPagination((prev) => ({
          ...prev,
          total: res?.data?.pagination?.totalRecords || 0,
        }));
      }
    } catch (error: any) {
      console.error(error?.response);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.current, pagination?.pageSize]);
 
  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };

  // ================= UI =================

  return (
    <div className="page-wrapper">
      <div className="content">
        {loading ? (
          <PageLoader />
        ) : (
          <>
            <PageBreadcrumb title="Assign LOB" breadcrumbs={breadcrumbItem} />

            <UserDetailsSection userDetails={currentManagerData} />

            <div className="card mt-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1">Select LOBs to Assign</h5>

                  <small className="text-muted">
                    Select the LOBs you want to assign to this manager.
                  </small>
                </div>

                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={handleAssignLOB}
                  disabled={selectedLobs.length === 0 || assignlobAPI.loading}
                >
                  {assignlobAPI.loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      Assigning...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-plus" />
                      Assign LOB
                    </>
                  )}
                </button>
              </div>
 
                <Datatable
                  dataSource={filteredData}
                  columns={columns}
                  fixedHeader
                  tableHeight={650}
                  Selection={false}
                  pagination={pagination}
                  handleTableChange={handleTableChange}
                /> 
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AddMoreLOB;
